import React, { useEffect, useState } from 'react'
import { FiArrowUpRight, FiFilter, FiX } from 'react-icons/fi'
import { salonAPI, resolveMediaUrl } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useStudio } from '../context/StudioContext'

export default function Gallery() {
  const { studio } = useStudio()
  const [images, setImages] = useState([])
  const [filteredImages, setFilteredImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedImage, setSelectedImage] = useState(null)

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await salonAPI.getGallery()
        setImages(response.data)
        setFilteredImages(response.data)
      } catch (error) {
        console.error('Error fetching gallery:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGallery()
  }, [])

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredImages(images)
    } else {
      setFilteredImages(images.filter((image) => image.category === selectedCategory))
    }
  }, [selectedCategory, images])

  const categories = ['All', ...new Set(images.map((image) => image.category))]

  return (
    <div className="pb-20">
      <section className="section-shell page-section">
        <div className="surface-card px-6 py-10 sm:px-10">
          <p className="eyebrow">Gallery</p>
          <h1 className="mt-4 font-heading text-5xl text-stone-900 sm:text-6xl">Public Instagram content curated into a cleaner studio showcase.</h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-stone-600 sm:text-base">
            These visuals were pulled from the public profile content that best fits the salon story: Triton Laser education, before-and-after results, brow detail, and studio-branded beauty imagery.
          </p>
        </div>
      </section>

      <section className="section-shell">
        <div className="surface-card p-6 sm:p-8">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-stone-600">
            <FiFilter />
            Filter by category
          </h2>
          <div className="mt-5 flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? 'filter-pill-active' : 'filter-pill'}
              >
                {category}
              </button>
            ))}
          </div>
          <a
            href={studio?.instagramUrl || 'https://www.instagram.com/maya__nails/'}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-stone-700"
          >
            Visit the live Instagram profile
            <FiArrowUpRight />
          </a>
        </div>
      </section>

      <section className="section-shell page-section">
        {loading ? (
          <LoadingSpinner />
        ) : filteredImages.length > 0 ? (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filteredImages.map((image) => (
              <button
                key={image.id}
                type="button"
                className="surface-card group overflow-hidden text-left"
                onClick={() => setSelectedImage(image)}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={resolveMediaUrl(image.imageUrl)}
                    alt={image.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                    <p className="text-xs uppercase tracking-[0.18em] text-white/70">{image.category}</p>
                    <h3 className="mt-2 font-heading text-4xl leading-none">{image.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm leading-7 text-stone-600">{image.description}</p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="surface-card p-10 text-center">
            <p className="font-heading text-4xl text-stone-900">No gallery items in this category.</p>
          </div>
        )}
      </section>

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="surface-card relative max-w-4xl overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-3 text-stone-700 shadow-sm"
              onClick={() => setSelectedImage(null)}
            >
              <FiX />
            </button>
            <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
              <div className="bg-stone-100">
                <img
                  src={resolveMediaUrl(selectedImage.imageUrl)}
                  alt={selectedImage.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-8">
                <p className="eyebrow">{selectedImage.category}</p>
                <h2 className="mt-4 font-heading text-5xl text-stone-900">{selectedImage.title}</h2>
                <p className="mt-5 text-sm leading-7 text-stone-600">{selectedImage.description}</p>
                <a
                  href={studio?.instagramUrl || 'https://www.instagram.com/maya__nails/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="secondary-button mt-8 inline-flex items-center gap-2"
                >
                  View the source profile
                  <FiArrowUpRight />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
